/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { DirectionalHint, ICalloutContentStyles, ICalloutProps } from '@fluentui/react/lib/Callout';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { Icon, IIconProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { ITagItemStyleProps, ITagItemStyles, TagItem } from '@fluentui/react/lib/Pickers';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps } from '@fluentui/react/lib/Text';
import { ITextFieldStyleProps, ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { AnimationClassNames } from '@fluentui/style-utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisContextualMenuInput } from 'src/core/components/LuisContextualMenuInput';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { LuisId } from 'src/luis/types/Resource';

const messages = defineMessages({
    pillEditIconAriaLabel: {
        id: 'core.pill.pillEditIconAriaLabel',
        defaultMessage: 'Edit value'
    },
    pillRemoveIconAriaLabel: {
        id: 'core.pill.pillRemoveIconAriaLabel',
        defaultMessage: 'Remove {value}'
    }
});

const pillTheme = getScopedTheme('Pill');
const { getClassName } = getStylistV2('Pill');

const pillIconStyle = classNamesFunction<IIconProps, IIconStyles>()({
    root: { fontSize: pillTheme.iconFontSize, paddingLeft: pillTheme.iconPaddingLeft }
});

const pillTagItemStyle = classNamesFunction<ITagItemStyleProps, ITagItemStyles>()({
    text: { marginRight: 0 },
    root: {
        margin: '5px 5px 5px 0',
        height: pillTheme.height,
        minHeight: pillTheme.height,
        border: `1px solid ${pillTheme.borderColor}`,
        ':focus-within': { background: 'unset', color: 'unset' }
    },
    close: {
        width: pillTheme.closeIconHeight,
        ':hover': { background: pillTheme.removeIconHoverBackgroundColor },
        ':active': { background: pillTheme.removeIconActiveBackgroundColor, color: pillTheme.removeIconActiveColor },
        ':focus-within': { background: pillTheme.removeIconActiveBackgroundColor, color: pillTheme.removeIconActiveColor }
    }
});

const disabledPill = getClassName('disabledPill', {
    textDecoration: 'line-through'
});

const pillInputTextFieldStyles = classNamesFunction<ITextFieldStyleProps, ITextFieldStyles>()({
    fieldGroup: {
        boxSizing: 'content-box',
        flexShrink: pillTheme.flexShrink,
        height: pillTheme.height,
        lineHeight: pillTheme.lineHeight,
        maxWidth: pillTheme.maxWidth,
        background: 'transparent',
        marginLeft: pillTheme.marginLeft
    },
    field: {
        padding: pillTheme.padding,
        fontSize: pillTheme.fontSize,
        selectors: { '::placeholder': { fontSize: pillTheme.fontSize, textOverflow: 'ellipsis' } }
    }
});

const contextualMenuTriggerStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { position: 'relative', top: pillTheme.calloutTopAdjust, left: pillTheme.calloutLeftAdjust }
});

const contextualMenuCalloutStyles = classNamesFunction<ICalloutProps, ICalloutContentStyles>()({
    root: { width: 'fit-content' }
});

/**
 * Default render of pill content.
 * @param props Pill props.
 */
const defaultRender = (props: PillProps) => (
    <LuisText {...props.textProps} className={props.isDisabled ? disabledPill : null} variant="smallPlus">
        {props.name}
    </LuisText>
);

export type PillProps = {
    /**
     * The pill unique key
     */
    pillKey: LuisId;
    /**
     * The index of the pill
     */
    index: number;

    /**
     * The pill text to display
     */
    name: string;

    /**
     * The initial edit mode the pill to be in.
     */
    initialEditMode?: boolean;

    /**
     * Boolean to determines whether to show the confirm icon or not.
     */
    showConfirmIcon?: boolean;

    /**
     * The menu items to display for the pill
     * text field and contextual menu.
     */
    menuItems?: IContextualMenuItem[];

    /**
     * The pill icon props.
     */
    iconProps?: IIconProps;

    /**
     * The pill text props.
     */
    textProps?: ITextProps;

    /**
     * The text to be displayed in the search box
     */
    pillInputTextPlaceholder?: string;

    /**
     * The pill is disabled or not.
     */
    isDisabled?: boolean;

    /**
     * The items to display when the filtered items
     * have a length of zero.
     */
    onNoItemsContent?: (query: string) => IContextualMenuItem[];

    /**
     * The items to display when the search query
     * doesn't fully match one item.
     */
    onNoExactQueryMatchContent?: (query: string) => IContextualMenuItem[];

    /**
     * Indicator if the already created pills should
     * be editable.
     */
    isPillEditable?: boolean;

    /**
     * A callback for when the remove icon on
     * a pill is clicked.
     */
    onRemovePill?: () => void;

    /**
     * A callback fires when a pill value is changed.
     */
    onPillValueChange?: (newName: string) => void;

    /**
     * A callback for when the pill is clicked.
     */
    onPillClick?: () => void;

    /**
     * An optional function to render a custom pill content.
     */
    onRenderPillContent?: (props?: PillProps, defaultRender?: (props: PillProps) => React.ReactNode) => React.ReactNode;

    /**
     * The delete button of the pill is disabled or not.
     */
    isDeleteButtonDisabled?: boolean;
};

/**
 * Renders a pill that supports:
 * - Inline editing in the label.
 * - Contextual menu items to display in a menu.
 * A pill or a Tag item is a component that have pill shape and contains text and other optional action buttons.
 * It's used in the roles / list entity / phrase list values and many other places in the portal.
 */
export const Pill = (props: PillProps) => {
    const {
        pillKey,
        name,
        index,
        iconProps,
        onPillClick,
        onRemovePill,
        isPillEditable,
        menuItems = [],
        onNoItemsContent,
        onPillValueChange,
        isDisabled = false,
        onRenderPillContent,
        initialEditMode = false,
        showConfirmIcon = false,
        pillInputTextPlaceholder,
        onNoExactQueryMatchContent,
        isDeleteButtonDisabled = false
    } = props;
    const { localize } = useLocalization();
    const [isEditModeActive, setIsEditModeActive] = React.useState(initialEditMode);

    /**
     * Notifies parent that a new value was added
     * in the pill.
     *
     * @param value The new value added.
     */
    const onSubmit = (value: string) => {
        onPillValueChange(value);
        setIsEditModeActive(false);
    };

    /**
     * Handles the case of pressing esc button to cancel.
     */
    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.key === LuisKeyCodes.Escape) {
            setIsEditModeActive(false);

            if (!name) {
                onRemovePill();
            }
        }
    };

    return (
        <TagItem
            index={index}
            role="listitem"
            styles={pillTagItemStyle}
            onRemoveItem={onRemovePill}
            item={{ key: `${pillKey}`, name }}
            removeButtonAriaLabel={localize(messages.pillRemoveIconAriaLabel, { value: name })}
            className={AnimationClassNames.fadeIn200}
            disabled={isDeleteButtonDisabled}>
            <Stack
                horizontal
                tokens={{ childrenGap: 5 }}
                styles={{ root: { cursor: isDisabled ? 'auto' : 'pointer' } }}
                verticalFill
                verticalAlign="center"
                onClick={onPillClick || (isPillEditable ? () => setIsEditModeActive(true) : undefined)}>
                {iconProps && <Icon {...iconProps} styles={pillIconStyle} />}

                {onRenderPillContent && onRenderPillContent(props, defaultRender)}

                {isEditModeActive ? (
                    <LuisContextualMenuInput
                        autoFocus
                        borderless
                        initValue={name}
                        onSubmit={onSubmit}
                        onKeyDown={onKeyDown}
                        menuItems={menuItems}
                        showConfirmIcon={showConfirmIcon}
                        styles={pillInputTextFieldStyles}
                        onNoItemsContent={onNoItemsContent}
                        onNoExactQueryMatchContent={onNoExactQueryMatchContent}
                        ariaLabel={pillInputTextPlaceholder}
                        placeholder={pillInputTextPlaceholder}
                        menuTriggerProps={{ styles: contextualMenuTriggerStyles }}
                        contextualMenuProps={{
                            directionalHint: DirectionalHint.bottomLeftEdge,
                            calloutProps: { styles: contextualMenuCalloutStyles }
                        }}
                    />
                ) : (
                    !onRenderPillContent && defaultRender(props)
                )}
            </Stack>
        </TagItem>
    );
};
